import {
  cloneElement,
  isValidElement,
  MouseEvent,
  ReactElement,
  ReactNode,
} from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

interface FilterChipProps {
  label: string;
  icon: ReactNode;
  count?: number;
  active: boolean;
  tooltip: string;
  onClick: (e: MouseEvent<HTMLElement>) => void;
  width?: number;
  showArrow?: boolean;
}

export const FilterChip = ({
  label,
  icon,
  count,
  active,
  tooltip,
  onClick,
  width = 140,
  showArrow = true,
}: Readonly<FilterChipProps>) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const badgeBg = isDark ? 'var(--color-background-paper, #1A1A1A)' : '#fff';
  const badgeTextColor = isDark ? '#fff' : theme.palette.text.primary;

  return (
    <Tooltip title={tooltip}>
      <Chip
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>{label}</span>
            {count !== undefined && count > 0 && (
              <Box
                sx={{
                  borderRadius: '9999px',
                  background: badgeBg,
                  border: isDark
                    ? 'none'
                    : `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  width: '18px',
                  height: '18px',
                  minWidth: '18px',
                  minHeight: '18px',
                  maxHeight: '18px',
                  padding: '0 5.4px',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    lineHeight: 1,
                    color: badgeTextColor,
                  }}
                >
                  {count}
                </Typography>
              </Box>
            )}
            {showArrow && <ArrowDropDownIcon sx={{ fontSize: 18, mr: -0.5 }} />}
          </Box>
        }
        icon={
          active && isValidElement(icon)
            ? (cloneElement(icon as ReactElement<any>, {
                sx: {
                  ...(icon.props?.sx ?? {}),
                  color: '#fff !important',
                  fontSize: 18,
                },
              }) as any)
            : (icon as any)
        }
        onClick={onClick}
        color={active ? 'primary' : 'default'}
        variant={active ? 'filled' : 'outlined'}
        sx={{
          borderRadius: 6,
          width,
          '& .MuiChip-icon': { fontSize: 18, ml: '8px', mr: '-4px' },
          '& .MuiChip-label': { overflow: 'visible' },
          ...(active && {
            color: '#fff',
            '&& .MuiChip-label': { color: '#fff' },
            '&& .MuiChip-icon': { color: '#fff' },
            '&& svg': { color: '#fff' },
          }),
        }}
      />
    </Tooltip>
  );
};
